import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { Plus, Edit, Trash2, LogOut, Calendar, MapPin, Euro, ClipboardList, Image as ImageIcon, CreditCard, Images, Flag, Eye, TrendingUp, BarChart3, Presentation } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { isAdmin, role, hasPermission, logout, loading: authLoading } = useAdmin();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [viewStats, setViewStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }
    // Se è un tecnico, redirect alla pagina statistiche
    if (role === 'tech') {
      navigate('/admin/statistics');
      return;
    }
    fetchEvents();
    fetchViewStats();
  }, [isAdmin, role, navigate]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchViewStats = async () => {
    try {
      const response = await axios.get(`${API}/events/stats/views`);
      setViewStats(response.data.slice(0, 10)); // Top 10
    } catch (error) {
      console.error('Error fetching view stats:', error);
    }
  };

  const handleDelete = async (eventId, eventTitle) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${eventTitle}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/events/${eventId}`);
      setEvents(events.filter(e => e.id !== eventId));
      alert('Evento eliminato con successo!');
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Errore durante l\'eliminazione');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Wait for auth context to restore session before redirecting
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Accesso Richiesto</h2>
          <p className="text-gray-600 mb-6">Devi effettuare il login per accedere a questa pagina.</p>
          <Link 
            to="/admin/login" 
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-block"
          >
            Vai al Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-blue-600 text-white py-6 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-blue-100 mt-1">Gestisci i tuoi eventi</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <LogOut className="mr-2" size={20} />
              Esci
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Actions */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Tutti gli Eventi ({events.length})</h2>
          <div className="flex gap-4 flex-wrap">
            <Link
              to="/admin/bookings"
              className="flex items-center bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors shadow-lg"
              data-testid="bookings-link"
            >
              <ClipboardList className="mr-2" size={20} />
              Prenotazioni
            </Link>
            <Link
              to="/admin/media"
              className="flex items-center bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-amber-700 transition-colors shadow-lg"
              data-testid="media-link"
            >
              <ImageIcon className="mr-2" size={20} />
              Media Eventi
            </Link>
            <Link
              to="/admin/galleries"
              className="flex items-center bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors shadow-lg"
              data-testid="galleries-link"
            >
              <Images className="mr-2" size={20} />
              Gallerie Pubbliche
            </Link>
            <Link
              to="/admin/countries"
              className="flex items-center bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-cyan-700 transition-colors shadow-lg"
              data-testid="countries-link"
            >
              <Flag className="mr-2" size={20} />
              Nazioni
            </Link>
            <Link
              to="/admin/payments"
              className="flex items-center bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors shadow-lg"
              data-testid="payments-link"
            >
              <CreditCard className="mr-2" size={20} />
              Pagamenti
            </Link>
            <Link
              to="/admin/statistics"
              className="flex items-center bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors shadow-lg"
              data-testid="statistics-link"
            >
              <BarChart3 className="mr-2" size={20} />
              Statistiche
            </Link>
            <Link
              to="/admin/analytics"
              className="flex items-center bg-violet-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-violet-700 transition-colors shadow-lg"
              data-testid="analytics-link"
            >
              <TrendingUp className="mr-2" size={20} />
              Analytics
            </Link>
            <Link
              to="/admin/corso"
              className="flex items-center bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors shadow-lg"
              data-testid="corso-link"
            >
              <Presentation className="mr-2" size={20} />
              Corso OsmAnd
            </Link>
            <Link
              to="/admin/events/new"
              className="flex items-center bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg"
            >
              <Plus className="mr-2" size={20} />
              Nuovo Evento
            </Link>
          </div>
        </div>

        {/* View Stats Section */}
        {viewStats.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <BarChart3 className="mr-2 text-purple-600" size={24} />
                Eventi Più Visitati
              </h2>
              <span className="text-sm text-gray-500">Utile per indirizzare la pubblicità</span>
            </div>
            <div className="space-y-3">
              {viewStats.map((event, index) => {
                const maxViews = viewStats[0]?.view_count || 1;
                const percentage = (event.view_count / maxViews) * 100;
                return (
                  <div key={event.id} className="flex items-center gap-4">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-700' :
                      index === 2 ? 'bg-amber-600 text-white' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="flex-grow">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900 truncate max-w-[200px] md:max-w-none">
                          {event.title_it}
                        </span>
                        <span className="flex items-center text-sm font-bold text-purple-600">
                          <Eye size={14} className="mr-1" />
                          {event.view_count || 0}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-4 pt-3 border-t">
              Queste statistiche ti aiutano a capire quali eventi attirano più interesse. 
              Concentra la pubblicità sugli eventi con meno visite ma alto potenziale.
            </p>
          </div>
        )}

        {/* Events List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {events.map((event) => (
              <div key={event.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="flex">
                  {/* Image */}
                  <div 
                    className="w-64 h-48 bg-cover bg-center flex-shrink-0"
                    style={{ backgroundImage: `url(${event.image_url})` }}
                  ></div>
                  
                  {/* Content */}
                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{event.title_it}</h3>
                        <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                          {event.event_type}
                        </span>
                      </div>
                      <div className="flex space-x-2">
                        <Link
                          to={`/admin/events/edit/${event.id}`}
                          className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Edit size={16} className="mr-1" />
                          Modifica
                        </Link>
                        <button
                          onClick={() => handleDelete(event.id, event.title_it)}
                          className="flex items-center bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                        >
                          <Trash2 size={16} className="mr-1" />
                          Elimina
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center text-gray-700">
                        <Calendar size={18} className="mr-2" />
                        <span className="text-sm">{event.date_start} - {event.date_end}</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <MapPin size={18} className="mr-2" />
                        <span className="text-sm">{event.location}</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <Euro size={18} className="mr-2" />
                        <span className="text-sm">
                          {event.price_rent && `Noleggio: €${event.price_rent}`}
                          {event.price_own && ` | Propria: €${event.price_own}`}
                        </span>
                      </div>
                      <div className="text-sm text-gray-700">
                        <strong>Posti:</strong> {event.spots_available || 'N/A'} / {event.spots_total || 'N/A'}
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm line-clamp-2">{event.description_it}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {events.length === 0 && !loading && (
          <div className="text-center py-12 bg-white rounded-xl shadow">
            <p className="text-gray-600 text-lg mb-4">Nessun evento trovato</p>
            <Link
              to="/admin/events/new"
              className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700"
            >
              <Plus className="mr-2" size={20} />
              Crea il tuo primo evento
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;